import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Save } from "lucide-react";
import DemoMode from "@/components/DemoMode";
import OcrProviderSetting from "@/components/OcrProviderSetting";

function ListEditor({ label, value, onChange, testId, hint }) {
    return (
        <div>
            <Label className="eyebrow">{label}</Label>
            <Textarea
                rows={4}
                value={(value || []).join("\n")}
                onChange={(e) => onChange(e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))}
                className="mt-1.5 font-mono-figure text-sm"
                placeholder="One per line"
                data-testid={testId}
            />
            {hint && <div className="text-[11px] text-stone-400 mt-1">{hint}</div>}
        </div>
    );
}

export default function AdminSettings() {
    const [s, setS] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get("/settings").then((r) => setS(r.data));
    }, []);

    if (!s) return <div className="text-stone-500 text-sm">Loading…</div>;

    const set = (k) => (v) => setS((prev) => ({ ...prev, [k]: v }));
    const setNested = (parent, k) => (v) => setS((prev) => ({ ...prev, [parent]: { ...(prev[parent] || {}), [k]: v } }));

    const save = async () => {
        setBusy(true);
        try {
            const payload = {
                expense_categories: s.expense_categories,
                banks: s.banks,
                card_networks: s.card_networks,
                payment_modes: s.payment_modes,
                notification_rules: s.notification_rules,
                approval_rules: s.approval_rules,
                session_timeout_minutes: Number(s.session_timeout_minutes),
                dashboard_preferences: s.dashboard_preferences,
                unusual_txn_threshold: Number(s.unusual_txn_threshold),
            };
            await api.put("/settings", payload);
            toast.success("Settings saved");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid="settings-page">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
                <div>
                    <div className="eyebrow">Admin</div>
                    <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">Settings.</h1>
                    <p className="text-sm text-stone-500 mt-3">Manage the lists that power dropdowns, alert thresholds and workflows.</p>
                </div>
                <Button onClick={save} disabled={busy} className="bg-stone-900 hover:bg-stone-800 text-white" data-testid="settings-save-btn">
                    <Save size={16} className="mr-1.5" /> {busy ? "Saving…" : "Save settings"}
                </Button>
            </div>

            {/* Demo Mode — one-click sample data for new users */}
            <DemoMode />

            {/* AI · OCR provider default (owner-only) */}
            <OcrProviderSetting />

            {/* AI · Merchant rules governance */}
            <div className="mb-8 rounded-xl border border-amber-200 bg-amber-50/30 p-5" data-testid="merchant-rules-link-card">
                <div className="flex items-baseline justify-between gap-3 flex-wrap">
                    <div>
                        <div className="text-[11px] uppercase tracking-widest text-amber-900 font-semibold">AI · Merchant rules</div>
                        <div className="text-sm text-stone-700 mt-1 max-w-xl">
                            Every time GPT categorises a merchant, VAULT saves a regex rule so the next
                            match is caught for free. Fix a bad classification once, it stops repeating.
                        </div>
                    </div>
                    <Link
                        to="/settings/merchant-rules"
                        className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-stone-900 text-white px-4 py-1.5 text-[12px] font-semibold hover:bg-stone-700"
                        data-testid="link-merchant-rules"
                    >
                        Manage rules →
                    </Link>
                </div>
            </div>

            {/* Email delivery — Resend sender / domain guidance */}
            <div className="mb-8 rounded-xl border border-blue-200 bg-blue-50/50 p-5" data-testid="resend-status-card">
                <div className="flex items-baseline justify-between gap-3 flex-wrap">
                    <div>
                        <div className="text-[11px] uppercase tracking-widest text-blue-900 font-semibold">Email delivery · Resend</div>
                        <div className="text-sm text-stone-700 mt-1">Password-reset and admin-notification emails ship via Resend.</div>
                    </div>
                    <a href="https://resend.com/domains" target="_blank" rel="noreferrer" className="text-sm font-medium text-blue-700 hover:text-blue-800 underline">
                        Manage domains ↗
                    </a>
                </div>
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                    <div className="rounded-md border border-blue-200 bg-white p-2.5">
                        <div className="eyebrow text-stone-500">Sender configured</div>
                        <div className="mt-1 font-mono-figure text-stone-800 text-sm truncate">
                            {process.env.REACT_APP_SENDER_EMAIL || "onboarding@resend.dev (sandbox)"}
                        </div>
                    </div>
                    <div className="rounded-md border border-blue-200 bg-white p-2.5">
                        <div className="eyebrow text-stone-500">Delivery today</div>
                        <div className="mt-1 text-stone-800 text-sm">
                            Only to <strong>info@suntronix.net</strong> — the Resend account owner (sandbox limit).
                        </div>
                    </div>
                    <div className="rounded-md border border-blue-200 bg-white p-2.5">
                        <div className="eyebrow text-stone-500">To unlock any recipient</div>
                        <ol className="mt-1 text-stone-700 space-y-0.5 pl-3.5 list-decimal">
                            <li>Add + verify a domain in Resend</li>
                            <li>Set <code>SENDER_EMAIL=noreply@yourdomain.com</code> in backend .env</li>
                            <li>Restart backend — no code change</li>
                        </ol>
                    </div>
                </div>
            </div>

            <div className="form-section space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <ListEditor label="Expense categories" value={s.expense_categories} onChange={set("expense_categories")} testId="settings-categories" hint="Shown in expense form category dropdown." />
                    <ListEditor label="Banks" value={s.banks} onChange={set("banks")} testId="settings-banks" hint="For quick-select in card master." />
                    <ListEditor label="Card networks" value={s.card_networks} onChange={set("card_networks")} testId="settings-networks" />
                    <ListEditor label="Payment modes" value={s.payment_modes} onChange={set("payment_modes")} testId="settings-payment-modes" />
                </div>

                <div className="divider-strong pt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <div className="eyebrow mb-3">Notification rules</div>
                        <div className="space-y-3">
                            <div>
                                <Label className="text-xs text-stone-600">Payment due reminders (days before)</Label>
                                <Input
                                    className="mt-1.5"
                                    value={(s.notification_rules?.payment_due_days || []).join(",")}
                                    onChange={(e) => setNested("notification_rules", "payment_due_days")(e.target.value.split(",").map(v => Number(v.trim())).filter(v => !isNaN(v)))}
                                    placeholder="7,3,1,0"
                                />
                            </div>
                            <div>
                                <Label className="text-xs text-stone-600">Card expiry warning (days)</Label>
                                <Input type="number" className="mt-1.5" value={s.notification_rules?.expiry_days || 60} onChange={(e) => setNested("notification_rules", "expiry_days")(Number(e.target.value))} />
                            </div>
                            <div>
                                <Label className="text-xs text-stone-600">High utilization alert (%)</Label>
                                <Input type="number" className="mt-1.5" value={s.notification_rules?.high_utilization_pct || 70} onChange={(e) => setNested("notification_rules", "high_utilization_pct")(Number(e.target.value))} />
                            </div>
                        </div>
                    </div>

                    <div>
                        <div className="eyebrow mb-3">Approval &amp; anomaly rules</div>
                        <div className="space-y-3">
                            <div>
                                <Label className="text-xs text-stone-600">Auto-approve expenses below</Label>
                                <Input type="number" className="mt-1.5" value={s.approval_rules?.auto_approve_below || 0} onChange={(e) => setNested("approval_rules", "auto_approve_below")(Number(e.target.value))} />
                            </div>
                            <div>
                                <Label className="text-xs text-stone-600">Require approval above</Label>
                                <Input type="number" className="mt-1.5" value={s.approval_rules?.require_approval_above || 0} onChange={(e) => setNested("approval_rules", "require_approval_above")(Number(e.target.value))} />
                            </div>
                            <div>
                                <Label className="text-xs text-stone-600">Unusual transaction threshold</Label>
                                <Input type="number" className="mt-1.5" value={s.unusual_txn_threshold || 0} onChange={(e) => set("unusual_txn_threshold")(Number(e.target.value))} data-testid="settings-unusual-threshold" />
                                <div className="text-[11px] text-stone-400 mt-1">Alerts flag any expense at or above this amount.</div>
                            </div>
                            <div>
                                <Label className="text-xs text-stone-600">Session timeout (minutes)</Label>
                                <Input type="number" className="mt-1.5" value={s.session_timeout_minutes || 30} onChange={(e) => set("session_timeout_minutes")(Number(e.target.value))} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
